import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { ConfirmModal } from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import {
  ChatCircle,
  PaperPlaneTilt,
  Robot,
  User,
  UserCircle,
  Power,
  MagnifyingGlass,
  ArrowLeft,
  Paperclip,
  FileDoc,
  X,
  Globe,
  Archive,
  Trash,
  Prohibit
} from '@phosphor-icons/react';

// Sort conversations by most recent activity (newest first)
const sortConvs = (list) =>
  [...list].sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));

export function Inbox() {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [contactsMap, setContactsMap] = useState({});
  const [searchMessages, setSearchMessages] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [typingConvId, setTypingConvId] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const fileInputRef = useRef(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [filteredReplies, setFilteredReplies] = useState([]);
  const [viewArchived, setViewArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // conversation pending deletion
  const [deleting, setDeleting] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(null); // conversation pending block
  const [blocking, setBlocking] = useState(false);
  const viewArchivedRef = useRef(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvPhone, setNewConvPhone] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateSid, setSelectedTemplateSid] = useState('');
  const [templateVars, setTemplateVars] = useState({});
  const [startingConv, setStartingConv] = useState(false);
  const [team, setTeam] = useState([]);
  const isOwner = user?.company_role !== 'agent';
  const teamMap = Object.fromEntries(team.map(m => [m.id, m.name || m.email]));
  const messagesEndRef = useRef(null);
  const selectedConvRef = useRef(null);
  const wsRef = useRef(null);
  const notifSoundRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  // Notification sound (base64 short beep)
  useEffect(() => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH+Onp6XjHdpZXeBkZ+dlIV0aGh5ipmfnJOFdGtsfIyanpqOgHJqb4CSmZ2YjH5xam2Bk5udl4t9cGxvgpSbnZaKfHBrcYOUm5yVinxwbHKElJuclYp8cGxyhJSbnJWKfHBscg==');
    audio.volume = 0.3;
    notifSoundRef.current = audio;
  }, []);

  const playNotifSound = () => {
    try {
      if (notifSoundRef.current) {
        notifSoundRef.current.currentTime = 0;
        notifSoundRef.current.play().catch(() => {});
      }
    } catch {}
  };

  // Keep ref in sync with state for WS handler
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
    fetchQuickReplies();
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await api.getTeam();
      setTeam(res.data || []);
    } catch {}
  };

  // Switching between active/archived reloads the list
  useEffect(() => {
    viewArchivedRef.current = viewArchived;
    setSelectedConv(null);
    setLoading(true);
    fetchConversations(viewArchived);
  }, [viewArchived]);

  const fetchContacts = async () => {
    try {
      const res = await api.getContacts();
      const map = {};
      for (const c of res.data) {
        map[c.phone] = c;
      }
      setContactsMap(map);
    } catch (e) {
      // contacts not critical
    }
  };

  const fetchQuickReplies = async () => {
    try {
      const res = await api.getQuickReplies();
      setQuickReplies(res.data || []);
    } catch {}
  };

  const handleInputChange = (value) => {
    setNewMessage(value);
    // Show quick reply suggestions when typing /
    if (value.startsWith('/') && value.length >= 1) {
      const query = value.toLowerCase();
      const matches = quickReplies.filter(r => r.shortcut.toLowerCase().startsWith(query));
      setFilteredReplies(matches);
      setShowQuickReplies(matches.length > 0);
    } else {
      setShowQuickReplies(false);
    }
  };

  const selectQuickReply = (reply) => {
    setNewMessage(reply.content);
    setShowQuickReplies(false);
  };

  const getContactName = (conv) => {
    const contact = contactsMap[conv.contact_phone];
    return contact?.name || conv.contact_name || conv.contact_phone;
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user?.company_id || !token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const wsUrl = backendUrl.replace('http', 'ws') + `/ws/${user.company_id}?token=${token}`;

    let ws;
    let reconnectTimeout;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_message') {
            const msg = data.message;
            // Clear typing indicator
            setTypingConvId(null);
            // Play sound for incoming contact messages
            if (msg.sender_type === 'contact') {
              playNotifSound();
            }
            // Update messages if this conversation is selected
            if (selectedConvRef.current?.id === data.conversation_id) {
              setMessages(prev => {
                if (prev.some(m => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
            }
            // Viewing archived: an incoming message revives the conversation server-side,
            // so it must leave this list -> just refetch
            if (viewArchivedRef.current) {
              fetchConversations();
              return;
            }
            // Update conversation list (move to top, bump unread badge for incoming)
            setConversations(prev => {
              // New conversation not in list yet -> reload full list
              if (!prev.some(c => c.id === data.conversation_id)) {
                fetchConversations();
                return prev;
              }
              const isIncoming = msg.sender_type === 'contact';
              const isOpen = selectedConvRef.current?.id === data.conversation_id;
              const updated = prev.map(c =>
                c.id === data.conversation_id
                  ? {
                      ...c,
                      last_message: msg.content,
                      last_message_at: msg.created_at,
                      unread_count: (c.unread_count || 0) + (isIncoming && !isOpen ? 1 : 0),
                    }
                  : c
              );
              return sortConvs(updated);
            });
          }

          if (data.type === 'template_delivery_failed') {
            toast.error(`La plantilla a ${data.contact_phone} no se entregó (error ${data.error_code})`);
          }

          if (data.type === 'typing') {
            setTypingConvId(data.conversation_id);
            // Auto-clear typing after 15s
            setTimeout(() => setTypingConvId(prev => prev === data.conversation_id ? null : prev), 15000);
          }

          if (data.type === 'escalation') {
            // Client requested human - update conversation to show bot is off
            setConversations(prev => prev.map(c =>
              c.id === data.conversation_id
                ? { ...c, is_bot_active: false, escalation_pending: true }
                : c
            ));
            if (selectedConvRef.current?.id === data.conversation_id) {
              setSelectedConv(prev => prev ? { ...prev, is_bot_active: false, escalation_pending: true } : prev);
            }
          }

          if (data.type === 'conversation_assigned') {
            setConversations(prev => prev.map(c =>
              c.id === data.conversation_id
                ? { ...c, assigned_to: data.user_id, escalation_pending: false }
                : c
            ));
            if (selectedConvRef.current?.id === data.conversation_id) {
              setSelectedConv(prev => prev ? { ...prev, assigned_to: data.user_id, escalation_pending: false } : prev);
            }
          }
        } catch (e) {
          // ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected, reconnecting in 3s...');
        reconnectTimeout = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [user?.company_id, token]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
    }
  }, [selectedConv]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-grow the message textarea (up to a max height) as the user types
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 128) + 'px';
    }
  }, [newMessage]);

  const fetchConversations = async (archived = viewArchivedRef.current) => {
    try {
      const res = await api.getConversations(undefined, archived);
      setConversations(sortConvs(res.data));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await api.getMessages(conversationId);
      setMessages(res.data);
      // Clear unread count locally (backend already marks as read)
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !pendingFile) || !selectedConv) return;
    setSending(true);

    try {
      let mediaUrl = null;
      let mediaType = null;

      // Upload file first if pending
      if (pendingFile) {
        setUploading(true);
        const uploadRes = await api.uploadFile(pendingFile);
        mediaUrl = uploadRes.data.media_url;
        mediaType = uploadRes.data.media_type;
        setUploading(false);
      }

      const content = newMessage.trim() || (mediaType === 'image' ? '' : '');
      const res = await api.sendMessage(selectedConv.id, content, mediaUrl, mediaType);
      setMessages([...messages, res.data]);
      setNewMessage('');
      setPendingFile(null);

      setConversations(sortConvs(conversations.map(c =>
        c.id === selectedConv.id
          ? { ...c, last_message: content || (mediaType === 'image' ? '[Imagen]' : '[Archivo]'), last_message_at: new Date().toISOString() }
          : c
      )));
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el mensaje');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Archivo demasiado grande. Máximo 10MB.');
      return;
    }
    setPendingFile(file);
  };

  const openNewConv = async () => {
    setShowNewConv(true);
    setNewConvPhone('');
    setSelectedTemplateSid('');
    setTemplateVars({});
    setTemplatesLoading(true);
    try {
      const res = await api.getTemplates();
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('No se pudieron cargar las plantillas');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.sid === selectedTemplateSid);

  const renderTemplatePreview = () => {
    if (!selectedTemplate) return '';
    let text = selectedTemplate.body;
    for (const v of selectedTemplate.variables) {
      text = text.split(`{{${v}}}`).join(templateVars[v] || `{{${v}}}`);
    }
    return text;
  };

  const handleStartConv = async () => {
    if (!newConvPhone.trim() || !selectedTemplate) return;
    const missing = selectedTemplate.variables.filter(v => !templateVars[v]?.trim());
    if (missing.length > 0) {
      toast.error(`Completa las variables: ${missing.join(', ')}`);
      return;
    }
    setStartingConv(true);
    try {
      const res = await api.startTemplateConversation(newConvPhone.trim(), selectedTemplate.sid, templateVars);
      toast.success('Plantilla enviada');
      setShowNewConv(false);
      if (viewArchived) setViewArchived(false);
      await fetchConversations(false);
      setSelectedConv(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la plantilla');
    } finally {
      setStartingConv(false);
    }
  };

  const handleClaim = async () => {
    if (!selectedConv) return;
    try {
      const res = await api.claimConversation(selectedConv.id);
      setSelectedConv(res.data);
      setConversations(prev => prev.map(c => (c.id === res.data.id ? res.data : c)));
      toast.success('Conversación asignada a ti');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al tomar la conversación');
    }
  };

  const handleAssign = async (userId) => {
    if (!selectedConv) return;
    try {
      const res = await api.assignConversation(selectedConv.id, userId);
      setSelectedConv(res.data);
      setConversations(prev => prev.map(c => (c.id === res.data.id ? res.data : c)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleBlock = async () => {
    if (!confirmBlock) return;
    setBlocking(true);
    try {
      await api.blockConversation(confirmBlock.id, true);
      toast.success(`${getContactName(confirmBlock)} bloqueado. Ya no recibirás sus mensajes.`);
      setConfirmBlock(null);
      setSelectedConv(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al bloquear');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    if (!selectedConv) return;
    try {
      const res = await api.blockConversation(selectedConv.id, false);
      setSelectedConv(res.data);
      setConversations(prev => prev.map(c => (c.id === res.data.id ? res.data : c)));
      toast.success('Contacto desbloqueado. Sus mensajes volverán a llegar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desbloquear');
    }
  };

  const handleArchive = async () => {
    if (!selectedConv) return;
    const archiving = !selectedConv.archived;
    try {
      await api.archiveConversation(selectedConv.id, archiving);
      toast.success(archiving ? 'Conversación archivada' : 'Conversación desarchivada');
      setSelectedConv(null);
      fetchConversations();
    } catch (error) {
      toast.error('Error al archivar la conversación');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.deleteConversation(confirmDelete.id);
      toast.success('Conversación eliminada permanentemente');
      setConfirmDelete(null);
      if (selectedConv?.id === confirmDelete.id) setSelectedConv(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la conversación');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleBot = async () => {
    if (!selectedConv) return;
    
    try {
      const res = await api.toggleBot(selectedConv.id, !selectedConv.is_bot_active, null);
      setSelectedConv(res.data);
      setConversations(conversations.map(c => 
        c.id === selectedConv.id ? res.data : c
      ));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filteredConversations = conversations.filter(c =>
    c.contact_phone.includes(search) ||
    c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
    contactsMap[c.contact_phone]?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return formatTime(dateString);
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'ayer';
    }
    const opts = { day: '2-digit', month: 'short' };
    if (date.getFullYear() !== today.getFullYear()) opts.year = '2-digit';
    return date.toLocaleDateString('es-MX', opts);
  };

  const isSameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

  // Label for the date separators inside the chat history
  const formatDayLabel = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Hoy';
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {})
    });
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-56px)] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-56px)] flex">
      {/* Conversations List */}
      <div className={`w-full lg:w-80 border-r border-[#E4E4E7] bg-white flex flex-col ${selectedConv ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-4 border-b border-[#E4E4E7]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg text-[#09090B]">Conversaciones</h2>
            <button
              onClick={openNewConv}
              data-testid="new-conv-btn"
              className="px-3 py-1.5 text-xs font-medium bg-[#0047FF] text-white rounded-xl hover:bg-[#0035CC] transition-colors"
              title="Iniciar conversación con una plantilla de WhatsApp"
            >
              + Nueva
            </button>
          </div>
          <div className="flex gap-0 mb-3 border border-[#E4E4E7] rounded-xl overflow-hidden">
            <button
              onClick={() => setViewArchived(false)}
              data-testid="tab-active"
              className={`flex-1 py-1.5 text-sm font-medium transition-colors ${
                !viewArchived ? 'bg-[#0047FF] text-white rounded-xl' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              Activas
            </button>
            <button
              onClick={() => setViewArchived(true)}
              data-testid="tab-archived"
              className={`flex-1 py-1.5 text-sm font-medium transition-colors ${
                viewArchived ? 'bg-[#0047FF] text-white rounded-xl' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              <span className="inline-flex items-center gap-1"><Archive size={14} /> Archivadas</span>
            </button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full pl-10 pr-4 py-2 border border-[#E4E4E7] text-sm focus:border-[#0047FF] focus:outline-none"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-8 text-center">
              {viewArchived ? (
                <Archive size={48} className="mx-auto text-[#A1A1AA] mb-4" />
              ) : (
                <ChatCircle size={48} className="mx-auto text-[#A1A1AA] mb-4" />
              )}
              <p className="text-[#52525B]">{viewArchived ? 'No hay conversaciones archivadas' : 'No hay conversaciones'}</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setSelectedConv(conv)}
                data-testid={`conv-${conv.id}`}
                className={`w-full p-4 text-left border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors ${
                  selectedConv?.id === conv.id ? 'bg-[#F4F4F5]' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#E4E4E7] flex items-center justify-center flex-shrink-0">
                    <UserCircle size={24} className="text-[#52525B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-[#09090B] truncate">
                        {getContactName(conv)}
                      </p>
                      <span className="text-xs text-[#A1A1AA] flex-shrink-0">
                        {formatDate(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {conv.contact_phone?.startsWith('web_') ? (
                        <Globe size={12} className="text-purple-500 flex-shrink-0" />
                      ) : (
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${conv.is_bot_active ? 'bg-[#0047FF]' : 'bg-green-500'}`} />
                      )}
                      <p className="text-sm text-[#52525B] truncate">{conv.last_message}</p>
                    </div>
                    {conv.blocked ? (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-[#52525B] bg-[#F4F4F5] border border-[#E4E4E7] rounded-full px-2 py-0.5">
                        <Prohibit size={10} /> Bloqueado
                      </span>
                    ) : conv.escalation_pending ? (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                        <User size={10} weight="fill" /> Necesita humano
                      </span>
                    ) : conv.assigned_to ? (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-medium text-[#52525B] bg-[#F4F4F5] rounded-full px-2 py-0.5">
                        <User size={10} /> {teamMap[conv.assigned_to] || 'Asignada'}
                      </span>
                    ) : null}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-[#0047FF] text-white rounded-full text-xs flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConv ? (
        <div className="flex-1 flex flex-col bg-[#F4F4F5]">
          {/* Chat Header */}
          <div className="h-16 bg-white border-b border-[#E4E4E7] px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedConv(null)}
                className="lg:hidden p-2 -ml-2"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="w-10 h-10 bg-[#E4E4E7] flex items-center justify-center">
                <UserCircle size={24} className="text-[#52525B]" />
              </div>
              <div>
                <p className="font-medium text-[#09090B]">
                  {getContactName(selectedConv)}
                </p>
                <p className="text-xs text-[#52525B]">{selectedConv.contact_phone}</p>
              </div>
              {contactsMap[selectedConv.contact_phone] && (
                <button
                  onClick={() => navigate(`/dashboard/contacts`)}
                  className="text-xs text-[#0047FF] hover:underline ml-2"
                >
                  Ver perfil
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedConv.escalation_pending && (
                <button
                  onClick={handleClaim}
                  data-testid="claim-conv-btn"
                  className="px-3 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                >
                  Tomar conversación
                </button>
              )}
              {isOwner && team.length > 1 ? (
                <select
                  value={selectedConv.assigned_to || ''}
                  onChange={(e) => handleAssign(e.target.value || null)}
                  data-testid="assign-select"
                  title="Asignar conversación"
                  className="hidden md:block text-xs border border-[#E4E4E7] rounded-lg px-2 py-2 bg-white text-[#52525B] max-w-[150px] focus:border-[#0047FF] focus:outline-none"
                >
                  <option value="">Sin asignar</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>{m.name || m.email}</option>
                  ))}
                </select>
              ) : selectedConv.assigned_to ? (
                <span className="hidden md:inline-flex items-center gap-1 text-xs text-[#52525B] bg-[#F4F4F5] rounded-full px-3 py-1.5">
                  <User size={12} /> {teamMap[selectedConv.assigned_to] || 'Asignada'}
                </span>
              ) : null}
              {selectedConv.blocked ? (
                <button
                  onClick={handleUnblock}
                  data-testid="unblock-conv-btn"
                  className="px-3 py-2 text-sm font-medium border border-[#E4E4E7] rounded-xl text-[#52525B] hover:border-[#0047FF] hover:text-[#0047FF] transition-colors flex items-center gap-1.5"
                >
                  <Prohibit size={16} /> Desbloquear
                </button>
              ) : (
                <button
                  onClick={() => setConfirmBlock(selectedConv)}
                  data-testid="block-conv-btn"
                  title="Bloquear contacto (spam)"
                  className="p-2 text-[#52525B] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Prohibit size={18} />
                </button>
              )}
              <button
                onClick={handleArchive}
                data-testid="archive-conv-btn"
                title={selectedConv.archived ? 'Desarchivar conversación' : 'Archivar conversación'}
                className="p-2 text-[#52525B] hover:text-[#0047FF] hover:bg-[#F4F4F5] transition-colors"
              >
                <Archive size={18} weight={selectedConv.archived ? 'fill' : 'regular'} />
              </button>
              {selectedConv.archived && (
                <button
                  onClick={() => setConfirmDelete(selectedConv)}
                  data-testid="delete-conv-btn"
                  title="Eliminar conversación permanentemente"
                  className="p-2 text-[#52525B] hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash size={18} />
                </button>
              )}
              <button
                onClick={handleToggleBot}
                data-testid="toggle-bot-btn"
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedConv.is_bot_active
                    ? 'bg-[#0047FF] text-white rounded-xl'
                    : 'bg-green-500 text-white rounded-xl'
                }`}
              >
                <Power size={16} />
                {selectedConv.is_bot_active ? 'Bot activo' : 'Humano'}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <Fragment key={msg.id}>
              {(i === 0 || !isSameDay(messages[i - 1].created_at, msg.created_at)) && (
                <div className="flex justify-center">
                  <span className="px-3 py-1 text-xs bg-white rounded-2xl shadow-sm border border-[#E4E4E7] text-[#52525B]">
                    {formatDayLabel(msg.created_at)}
                  </span>
                </div>
              )}
              <div
                className={`flex ${msg.sender_type === 'contact' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[80%] ${msg.sender_type === 'contact' ? 'order-2' : 'order-1'}`}>
                  <div className={`px-4 py-3 ${
                    msg.sender_type === 'contact'
                      ? 'bg-white rounded-2xl shadow-sm border border-[#E4E4E7]'
                      : msg.sender_type === 'bot'
                        ? 'bg-[#0047FF] text-white rounded-xl'
                        : 'bg-green-500 text-white rounded-xl'
                  }`}>
                    {msg.media_url && msg.media_type === 'image' && (
                      <img
                        src={msg.media_url}
                        alt="Imagen"
                        className="max-w-full max-h-60 rounded mb-2 cursor-pointer"
                        onClick={() => setLightboxImg(msg.media_url)}
                      />
                    )}
                    {msg.media_url && msg.media_type === 'audio' && (
                      <audio
                        controls
                        preload="metadata"
                        src={msg.media_url}
                        className="max-w-full mb-2 h-10"
                      >
                        Tu navegador no soporta audio.
                      </audio>
                    )}
                    {msg.media_url && msg.media_type === 'video' && (
                      <video
                        controls
                        preload="metadata"
                        src={msg.media_url}
                        className="max-w-full max-h-60 rounded mb-2"
                      >
                        Tu navegador no soporta video.
                      </video>
                    )}
                    {msg.media_url && msg.media_type === 'document' && (
                      <a
                        href={msg.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 mb-2 text-sm underline ${msg.sender_type === 'contact' ? 'text-[#0047FF]' : 'text-white'}`}
                      >
                        <FileDoc size={16} /> Abrir documento
                      </a>
                    )}
                    {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                  </div>
                  <div className={`flex items-center gap-1 mt-1 text-xs text-[#A1A1AA] ${
                    msg.sender_type === 'contact' ? 'justify-start' : 'justify-end'
                  }`}>
                    {msg.sender_type === 'bot' && <Robot size={12} />}
                    {msg.sender_type === 'human' && <User size={12} />}
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
              </Fragment>
            ))}
            {typingConvId === selectedConv?.id && (
              <div className="flex justify-end">
                <div className="bg-[#0047FF]/10 px-4 py-3 flex items-center gap-2">
                  <Robot size={14} className="text-[#0047FF]" />
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#0047FF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#0047FF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#0047FF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-[#0047FF]">Bot respondiendo...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-[#E4E4E7]">
            {/* Quick Reply Suggestions */}
            {showQuickReplies && (
              <div className="mb-2 border border-[#E4E4E7] bg-white shadow-lg rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                {filteredReplies.map((reply) => (
                  <button
                    key={reply.id}
                    onClick={() => selectQuickReply(reply)}
                    className="w-full text-left px-4 py-2 hover:bg-[#F4F4F5] transition-colors border-b border-[#E4E4E7] last:border-b-0"
                  >
                    <span className="font-mono text-xs text-[#0047FF]">{reply.shortcut}</span>
                    <p className="text-sm text-[#52525B] truncate">{reply.content}</p>
                  </button>
                ))}
              </div>
            )}
            {/* Pending file preview */}
            {pendingFile && (
              <div className="mb-2 p-2 bg-[#F4F4F5] border border-[#E4E4E7] flex items-center gap-3">
                {pendingFile.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(pendingFile)} alt="Preview" className="w-16 h-16 object-cover rounded" />
                ) : (
                  <div className="w-16 h-16 bg-[#E4E4E7] flex items-center justify-center rounded"><FileDoc size={24} className="text-[#52525B]" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#09090B] truncate">{pendingFile.name}</p>
                  <p className="text-xs text-[#A1A1AA]">{(pendingFile.size / 1024).toFixed(0)} KB</p>
                </div>
                <button onClick={() => setPendingFile(null)} className="p-1 text-[#A1A1AA] hover:text-red-500"><X size={16} /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*,.pdf,.doc,.docx" className="hidden" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={selectedConv.is_bot_active}
                className="w-12 h-12 flex items-center justify-center text-[#A1A1AA] hover:text-[#0047FF] hover:bg-[#F4F4F5] transition-colors disabled:opacity-50"
                title="Adjuntar archivo"
              >
                <Paperclip size={20} />
              </button>
              <textarea
                ref={textareaRef}
                rows={1}
                value={newMessage}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowQuickReplies(false);
                    return;
                  }
                  // Enter sends; Shift+Enter inserts a line break
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (showQuickReplies && filteredReplies.length > 0) {
                      selectQuickReply(filteredReplies[0]);
                    } else {
                      handleSend();
                    }
                  }
                }}
                placeholder="Escribe un mensaje... (usa / para respuestas rápidas, Shift+Enter para salto de línea)"
                disabled={selectedConv.is_bot_active}
                data-testid="message-input"
                className="flex-1 px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none disabled:bg-[#F4F4F5] disabled:cursor-not-allowed resize-none max-h-32 overflow-y-auto leading-snug"
              />
              <button
                onClick={handleSend}
                disabled={(!newMessage.trim() && !pendingFile) || sending || selectedConv.is_bot_active}
                data-testid="send-message-btn"
                className="w-12 h-12 bg-[#0047FF] text-white rounded-xl flex items-center justify-center hover:bg-[#0035CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : <PaperPlaneTilt size={20} />}
              </button>
            </div>
            {selectedConv.is_bot_active && (
              <p className="text-xs text-[#A1A1AA] mt-2">
                El bot está respondiendo. Desactívalo para escribir manualmente.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center bg-[#F4F4F5]">
          <div className="text-center">
            <ChatCircle size={64} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="text-[#52525B]">Selecciona una conversación</p>
          </div>
        </div>
      )}

      {/* New conversation via template modal */}
      {showNewConv && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#09090B]">Nueva conversación</h3>
                <p className="text-xs text-[#52525B] mt-1">Envía una plantilla aprobada de WhatsApp a cualquier número</p>
              </div>
              <button onClick={() => setShowNewConv(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Número de WhatsApp <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={newConvPhone}
                  onChange={(e) => setNewConvPhone(e.target.value)}
                  placeholder="+52 33 1234 5678"
                  data-testid="new-conv-phone"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">Incluye la lada del país (ej. +52 para México).</p>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Plantilla <span className="text-red-500">*</span>
                </label>
                {templatesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[#52525B] py-3">
                    <div className="animate-spin w-4 h-4 border-2 border-[#0047FF] border-t-transparent rounded-full" />
                    Cargando plantillas...
                  </div>
                ) : templates.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <p className="text-sm text-yellow-700">
                      Aún no tienes plantillas aprobadas. Créalas en <span className="font-semibold">Configuración → Plantillas de WhatsApp</span> (planes
                      Pro y Business); te notificaremos cuando Meta las apruebe.
                    </p>
                  </div>
                ) : (
                  <select
                    value={selectedTemplateSid}
                    onChange={(e) => { setSelectedTemplateSid(e.target.value); setTemplateVars({}); }}
                    data-testid="new-conv-template"
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                  >
                    <option value="">Selecciona una plantilla...</option>
                    {templates.map((t) => (
                      <option key={t.sid} value={t.sid}>
                        {t.name} {t.category ? `(${t.category.toLowerCase()})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {selectedTemplate && selectedTemplate.variables.length > 0 && (
                <div className="space-y-3">
                  {selectedTemplate.variables.map((v) => (
                    <div key={v}>
                      <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-1">
                        Variable {`{{${v}}}`} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={templateVars[v] || ''}
                        onChange={(e) => setTemplateVars(prev => ({ ...prev, [v]: e.target.value }))}
                        placeholder={`Valor para {{${v}}}`}
                        className="w-full px-4 py-2.5 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}

              {selectedTemplate && (
                <div>
                  <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Vista previa</p>
                  <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-4">
                    <p className="text-sm text-[#09090B] whitespace-pre-wrap">{renderTemplatePreview()}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowNewConv(false)}
                disabled={startingConv}
                className="flex-1 py-2.5 border border-[#E4E4E7] text-[#09090B] font-medium hover:bg-[#F4F4F5] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleStartConv}
                disabled={startingConv || !newConvPhone.trim() || !selectedTemplate}
                data-testid="new-conv-send"
                className="flex-1 py-2.5 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {startingConv ? 'Enviando...' : 'Enviar plantilla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block contact confirmation */}
      <ConfirmModal
        open={!!confirmBlock}
        title="Bloquear contacto"
        confirmLabel="Bloquear"
        danger
        processing={blocking}
        onConfirm={handleBlock}
        onCancel={() => setConfirmBlock(null)}
      >
        <div className="space-y-3">
          <p className="text-sm text-[#52525B]">
            Vas a bloquear a{' '}
            <span className="font-medium text-[#09090B]">{confirmBlock ? getContactName(confirmBlock) : ''}</span>.
          </p>
          <ul className="text-sm text-[#52525B] space-y-1 list-disc pl-5">
            <li>Sus mensajes nuevos se <span className="font-medium text-[#09090B]">descartarán</span> (no aparecerán en el Inbox)</li>
            <li>El bot no le responderá ni le enviará seguimientos</li>
            <li>La conversación se moverá a Archivadas con la etiqueta "Bloqueado"</li>
          </ul>
          <p className="text-xs text-[#A1A1AA]">Puedes desbloquearlo cuando quieras desde la conversación archivada.</p>
        </div>
      </ConfirmModal>

      {/* Delete conversation confirmation */}
      <ConfirmModal
        open={!!confirmDelete}
        title="Eliminar conversación"
        confirmLabel="Eliminar permanentemente"
        danger
        processing={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      >
        <div className="space-y-3">
          <p className="text-sm text-[#52525B]">
            Vas a eliminar la conversación con{' '}
            <span className="font-medium text-[#09090B]">
              {confirmDelete ? getContactName(confirmDelete) : ''}
            </span>{' '}
            y <span className="font-medium text-[#09090B]">todos sus mensajes</span>.
          </p>
          <div className="bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-700 font-medium">Esta acción es permanente y no se puede deshacer.</p>
          </div>
          <p className="text-xs text-[#A1A1AA]">El contacto se conserva en tu directorio; si vuelve a escribir se creará una conversación nueva.</p>
        </div>
      </ConfirmModal>

      {/* Lightbox for images */}
      {lightboxImg && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
        >
          <button onClick={() => setLightboxImg(null)} className="absolute top-4 right-4 text-white"><X size={28} /></button>
          <img src={lightboxImg} alt="Imagen" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
